// ============================================================
// SESSION STORAGE - localStorage persistence for session state.
// Allows page-reload recovery and GM rejoin.
// ============================================================
import { isSafeDocId } from '../engine/importIds';

const SESSION_KEY = 'dw-session';

/** Max age for stored sessions - after this, don't offer rejoin */
export const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface StoredSession {
  id: string;
  code: string;
  isGm: boolean;
  playerId: string;
  displayName: string;
  storedAt: number;
}

/** Save session state to localStorage */
export function saveSession(session: StoredSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // localStorage full or unavailable - fail silently
  }
}

/** The fields a stored session cannot do without, each a string. */
const REQUIRED_STRINGS = ['id', 'playerId', 'code'] as const;

/**
 * Shape validation: an object carrying every required string, and a session
 * id and seat that can name a document (`sessions/{id}`, `players/{seat}`).
 * Both come from the server, so a bad one is not remapped (the server knows
 * no stand-in): it is a corrupt or edited store, and not a session to rejoin.
 */
function hasSessionShape(parsed: unknown): boolean {
  if (!parsed || typeof parsed !== 'object') return false;
  const o = parsed as Record<string, unknown>;
  return REQUIRED_STRINGS.every((k) => typeof o[k] === 'string') && isSafeDocId(o.id) && isSafeDocId(o.playerId);
}

/** Get stored session, or null if none / corrupted / expired */
export function getStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!hasSessionShape(parsed)) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    // Expiry check
    if (typeof parsed.storedAt === 'number' && Date.now() - parsed.storedAt > SESSION_MAX_AGE_MS) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed as StoredSession;
  } catch {
    return null;
  }
}

/** Clear stored session */
export function clearStoredSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // fail silently
  }
}
